import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { ClipboardList, Plus, Edit, Trash2, CreditCard, ArrowRightLeft, GitMerge, Shield, Search } from 'lucide-react';
import { Pagination } from '../components/shared/Pagination';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_ICONS = {
  create: <Plus className="w-4 h-4 text-green-600" />,
  update: <Edit className="w-4 h-4 text-blue-600" />,
  delete: <Trash2 className="w-4 h-4 text-red-600" />,
  payment: <CreditCard className="w-4 h-4 text-emerald-600" />,
  status_change: <ArrowRightLeft className="w-4 h-4 text-amber-600" />,
  merge: <GitMerge className="w-4 h-4 text-purple-600" />,
  permission_change: <Shield className="w-4 h-4 text-indigo-600" />,
};

const ACTION_COLORS = {
  create: 'bg-green-50 text-green-700 border-green-200',
  update: 'bg-blue-50 text-blue-700 border-blue-200',
  delete: 'bg-red-50 text-red-700 border-red-200',
  payment: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  status_change: 'bg-amber-50 text-amber-700 border-amber-200',
  merge: 'bg-purple-50 text-purple-700 border-purple-200',
  permission_change: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

const ENTITY_TYPES = ['order', 'customer', 'product', 'purchase', 'expense', 'asset', 'liability', 'capital', 'transfer', 'user'];
const ACTION_TYPES = ['create', 'update', 'delete', 'payment', 'status_change', 'merge'];

const AuditLogPage = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const ITEMS_PER_PAGE = 30;

  useEffect(() => {
    fetchEntries();
  }, [currentPage, actionFilter, entityFilter]);

  const fetchEntries = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('limit', ITEMS_PER_PAGE);
      if (actionFilter) params.append('action', actionFilter);
      if (entityFilter) params.append('entity_type', entityFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await axios.get(`${API_URL}/audit-log?${params.toString()}`);
      setEntries(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchEntries();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const d = new Date(timestamp);
    return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-primary" />
          Activity Log
        </h1>
        <p className="text-muted-foreground mt-1">Track all changes made across the system</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3" data-testid="audit-filters">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search activity..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-9"
            data-testid="audit-search"
          />
        </div>
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v === 'all' ? '' : v); setCurrentPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="audit-action-filter">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {ACTION_TYPES.map(a => <SelectItem key={a} value={a}>{a.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v === 'all' ? '' : v); setCurrentPage(1); }}>
          <SelectTrigger className="w-[160px]" data-testid="audit-entity-filter">
            <SelectValue placeholder="All Entities" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Entities</SelectItem>
            {ENTITY_TYPES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{total} entries</span>
      </div>

      {/* Activity Table */}
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="audit-log-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">When</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Action</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Entity</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entries.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                    No activity recorded yet. Actions will appear here as users make changes.
                  </td>
                </tr>
              )}
              {entries.map((entry, index) => (
                <tr key={index} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                    {formatDate(entry.timestamp)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">{entry.user_name}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${ACTION_COLORS[entry.action] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                      {ACTION_ICONS[entry.action] || null}
                      {entry.action?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="inline-block px-2 py-0.5 rounded bg-muted text-muted-foreground text-xs font-medium capitalize">
                      {entry.entity_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground max-w-md truncate">{entry.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-4">
          <Pagination page={currentPage} pages={totalPages} total={total} limit={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
        </div>
      </div>
    </div>
  );
};

export default AuditLogPage;
